import { ValueOf } from '../interfaces';

const PREFIX = '/portal';
const CORP_ADMIN = `${PREFIX}/tenant/:tenantId/admin`;

/**
 * 全量路由key
 */
export const SiteRouterKey = {
    MessageCenter: 'MESSAGE_CENTER', // 消息中心
    TemplateFile: 'TEMPLATE_FILE', // 模板文件下载
    /**
     * 通讯录导入模板专用下载路由
     * 提供后台获取租户上下文的能力
     */
    AdminTemplateFile: 'ADMIN_TEMPLATE_FILE',
    IdentityVerify: 'IDENTITY_VERIFY', // 身份认证
    CorpAdmin: 'CORP_ADMIN_INDEX', // 企业管理后台
    Account: 'ACCOUNT_INDEX', // 个人设置
    SignOut: 'SIGNOUT', // 退出登录
    OauthConsent: 'OAUTH_CONSENT', // 帆软通行证获取授权页面
    OpenStorePlugin: 'OPEN_STORE_PLUGIN', // 市场插件详情
    OpenPluginDependency: 'OPEN_PLUGIN_DEPENDENCY', // 插件依赖管理
    OpenPluginEdit: 'OPEN_PLATFORM_PUBLISH', // 自建插件编辑
    /** 开放平台 MCP 管理 */
    OpenPlatformMcp: 'OPEN_PLATFORM_MCP',
    /** 移动端切换企业页面 */
    SwitchCorp: 'SWITCH_CORP_M',
    /** 切换企业后跳转的中间页 */
    CorpSwitchRedirect: 'CorpSwitchRedirect',
    OrderCreate: 'ORDER_CREATE', // 订单创建页
    /** 企业画像 */
    Persona: 'PERSONA',
    ForgotPassword: 'ForgotPassword',
    Register: 'Register',
    /** 登录重定向 */
    SignInLaunch: 'SignInLaunch',
    /** 企业设置 */
    CorpSetting: 'CorpSetting',
    /** AI 能力设置 */
    AiLab: 'AiLab',
    /** 知识库首页 */
    KmsHome: 'KmsHome',
    VersionInfo: 'VersionInfo',
    CorpOrder: 'CorpOrder',
    Contact: 'Contact',
    CorpCoop: 'CorpCoop',
    ProductCenter: 'ProductCenter',
    PermissionQuery: 'AuthQuery',
    ProductSetting: 'ProductSetting',
    CorpLogin: 'CorpLogin',
    /** 跨域重定向 */
    SignInBridge: 'SignInBridge',
    /** 时区不兼容提示页 */
    TzSupport: 'TzSupport',
    /** 错误页(403/404/409) */
    Error: 'ERROR',
    CorpCoopM: 'MobileCorpCoop',
    /** 钉钉免登后默认跳转地址 */
    DingtalkAuth: 'DingtalkAuth',
    SignIn: 'SignIn'
} as const;

export type SiteRouterKey = ValueOf<typeof SiteRouterKey>;

type SiteRouterConfig = Record<SiteRouterKey, string>;

/**
 * 全局路由注册中心，返回全量路由定义，业务按需取用
 *
 */
export const getSiteRouterConfig = () => {
    return {
        [SiteRouterKey.MessageCenter]: `${PREFIX}/v1/message_center`, // SITE_ROUTER.MESSAGE
        [SiteRouterKey.TemplateFile]: `${PREFIX}/template/file`,
        [SiteRouterKey.AdminTemplateFile]: `${PREFIX}/tenant/:tenantId/admin/template/file`,
        [SiteRouterKey.IdentityVerify]: `${PREFIX}/tenant/:tenantId/admin/verify`, // SITE_ROUTER.TENANT_VERIFY.INDEX
        [SiteRouterKey.CorpAdmin]: `${PREFIX}/tenant/:tenantId/admin`, // SITE_ROUTER.CORP_ADMIN
        [SiteRouterKey.Account]: `${PREFIX}/account`, // SITE_ROUTER.ACCOUNT.INDEX
        [SiteRouterKey.SignOut]: `${PREFIX}/signout`, // SITE_ROUTER.AUTHENTICATION.SIGNOUT
        [SiteRouterKey.OauthConsent]: `${PREFIX}/oidc/authorize`,
        [SiteRouterKey.OpenStorePlugin]: '/open/store/plugin', // SITE_ROUTER.OPEN_STORE_PLUGIN
        [SiteRouterKey.OpenPluginDependency]: '/open#/dev/private_plugin/dependency', // ROUTER.OPEN.PRIVATE_PLUGIN_DEPENDENCY
        [SiteRouterKey.OpenPluginEdit]: '/open/owner/plugin', // SITE_ROUTER.OPEN_PLATFORM_PUBLISH
        [SiteRouterKey.OpenPlatformMcp]: '/open#/mcp', // ROUTER.OPEN.MCP
        [SiteRouterKey.SwitchCorp]: `${PREFIX}/account/select_corp`,
        [SiteRouterKey.CorpSwitchRedirect]: `${PREFIX}/account/switch_corp`, // SITE_ROUTER.ACCOUNT.SWITCH_CORP
        [SiteRouterKey.OrderCreate]: `${PREFIX}/tenant/:tenantId/admin/order/create`, // SITE_ROUTER.ORDER.CREATE
        [SiteRouterKey.Persona]: `${PREFIX}/persona`,
        [SiteRouterKey.ForgotPassword]: `${PREFIX}/authn/forgot_password`,
        [SiteRouterKey.Register]: `${PREFIX}/register`,
        [SiteRouterKey.SignInLaunch]: `${PREFIX}/signin/launch`,
        [SiteRouterKey.CorpSetting]: `${CORP_ADMIN}#/corp_setting`,
        [SiteRouterKey.AiLab]: '/ai/ai_setting',
        [SiteRouterKey.KmsHome]: '/spaces',
        [SiteRouterKey.VersionInfo]: `${CORP_ADMIN}#/vip`,
        [SiteRouterKey.CorpOrder]: `${CORP_ADMIN}#/order`,
        [SiteRouterKey.Contact]: `${CORP_ADMIN}#/internal`,
        [SiteRouterKey.CorpCoop]: `${CORP_ADMIN}#/interconnected`,
        [SiteRouterKey.ProductCenter]: `${CORP_ADMIN}#/product_center`,
        [SiteRouterKey.PermissionQuery]: `${CORP_ADMIN}#/auth_query/:tabKey`,
        [SiteRouterKey.ProductSetting]: `${CORP_ADMIN}#/suite_setting`,
        [SiteRouterKey.CorpLogin]: `${PREFIX}/tenant/:tenantId/signin`,
        [SiteRouterKey.SignInBridge]: `${PREFIX}/signin/bridge`,
        [SiteRouterKey.TzSupport]: `${PREFIX}/error/tz_support`,
        [SiteRouterKey.Error]: `${PREFIX}/error/:errorType`,
        [SiteRouterKey.CorpCoopM]: `${PREFIX}/tenant/:tenantId/admin_mobile#/team/external`,
        [SiteRouterKey.SignIn]: `${PREFIX}/signin`,
        [SiteRouterKey.DingtalkAuth]: `${PREFIX}/integrate/dingtalk/isv/:integrateCorpId/:suiteId/:suiteScenario/member/web/pc/v1/auth`
    } satisfies SiteRouterConfig;
};
